import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';
import type { LevelsFile } from './levels-types';

const LEVELS = (levelsData as LevelsFile).levels;

interface Question {
  digit: string;
  representation: 'hands' | 'dots';
  options: string[];   // digitos opcionais a serem oferecidos (apenas um e o correto)
}

export function mountLearnDigits(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }
  const config = level.data[0]!;
  const questions = buildQuestions(config.questionsArray, config.circlesModel, config.randomOrder);

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Aprendendo os números</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage learn-digits">
      <div class="feedback" aria-live="polite"></div>
      <p class="learn-digits__progress" aria-live="polite"></p>
      <div class="learn-digits__prompt">
        <span class="learn-digits__label">Quantos?</span>
        <img class="learn-digits__image" alt="" />
      </div>
      <div class="learn-digits__options" role="radiogroup"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.learn-digits__progress')!;
  const imageEl = page.querySelector<HTMLImageElement>('.learn-digits__image')!;
  const optionsEl = page.querySelector<HTMLElement>('.learn-digits__options')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  let index = 0;
  let correctCount = 0;
  let locked = false;

  function renderQuestion(): void {
    if (index >= questions.length) {
      finishLevel();
      return;
    }
    const q = questions[index]!;
    progressEl.textContent = `Questão ${index + 1} de ${questions.length}`;
    imageEl.src = `assets/gcompris-AGPL/learn_digits/${q.representation}/${q.digit}.svg`;
    imageEl.alt = `Imagem representando o número ${q.digit}`;
    optionsEl.innerHTML = '';
    locked = false;
    for (const opt of q.options) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'learn-digits__option';
      btn.setAttribute('role', 'radio');
      btn.textContent = opt;
      btn.addEventListener('click', () => choose(opt, btn));
      optionsEl.appendChild(btn);
    }
  }

  function choose(opt: string, btn: HTMLButtonElement): void {
    if (locked) return;
    locked = true;
    const q = questions[index]!;
    if (opt === q.digit) {
      ctx.audio.play('correct');
      correctCount++;
      btn.classList.add('learn-digits__option--ok');
      showFeedback('ok');
      window.setTimeout(() => {
        index++;
        renderQuestion();
      }, 700);
    } else {
      ctx.audio.play('wrong');
      btn.classList.add('learn-digits__option--err');
      showFeedback('err');
      window.setTimeout(() => {
        btn.classList.remove('learn-digits__option--err');
        locked = false;
      }, 700);
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 600);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${questions.length}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/learn_digits/${level.level + 1}`;
      });
    }
  }

  renderQuestion();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}

function buildQuestions(digits: string[], optionsCount: number, randomOrder: boolean): Question[] {
  const order = randomOrder ? shuffle([...digits]) : [...digits];
  return order.map((digit, i) => ({
    digit,
    representation: i % 2 === 0 ? 'hands' : 'dots',
    options: buildOptions(digit, digits, optionsCount),
  }));
}

function buildOptions(correct: string, pool: string[], count: number): string[] {
  const set = new Set<string>([correct]);
  const others = pool.filter((d) => d !== correct);
  shuffle(others);
  while (set.size < count && others.length > 0) {
    set.add(others.shift()!);
  }
  // Caso o pool seja pequeno, adiciona digitos de fora do questionsArray pra completar
  const fillPool = '0123456789'.split('').filter((d) => !set.has(d));
  shuffle(fillPool);
  while (set.size < count && fillPool.length > 0) {
    set.add(fillPool.shift()!);
  }
  return shuffle([...set]);
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
